import { useMemo } from 'react'
import { collection, query, where } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import type { ClassifiedError } from '../lib/errors'
import type {
  EventReport,
  EventSuggestion,
  Feedback,
  FirestoreTimestamp,
  RegionRequest,
  ScheduleLead,
  SetReport,
} from '../types'

export type RequestKind = 'suggestion' | 'lead' | 'feedback' | 'region' | 'setReport' | 'eventReport'

// One row of the My requests panel. The collections have nothing in
// common but "a user asked for something", so rather than flattening them into
// a lossy shared shape each row keeps its doc and the renderer switches on kind.
export type RequestItem =
  | { kind: 'suggestion'; id: string; createdAt: number; doc: EventSuggestion }
  | { kind: 'lead'; id: string; createdAt: number; doc: ScheduleLead }
  | { kind: 'feedback'; id: string; createdAt: number; doc: Feedback }
  | { kind: 'region'; id: string; createdAt: number; doc: RegionRequest }
  | { kind: 'setReport'; id: string; createdAt: number; doc: SetReport }
  | { kind: 'eventReport'; id: string; createdAt: number; doc: EventReport }

function millis(ts: FirestoreTimestamp | undefined): number {
  return ts?.toMillis?.() ?? 0
}

/**
 * Epoch millis of the moment a request reached a success-terminal state, or
 * null while it hasn't. This is what the unread badge compares against the
 * watermark: failures, dismissals and noise never badge — there's nothing for
 * the user to go and see. A terminal status with no stamp (legacy docs) falls
 * back to createdAt so it counts once and then clears.
 */
export function terminalMillis(item: RequestItem): number | null {
  switch (item.kind) {
    case 'suggestion': {
      const { status, fulfilledAt, processedAt } = item.doc
      if (status !== 'seeded' && status !== 'multi') return null
      return millis(fulfilledAt) || millis(processedAt) || item.createdAt
    }
    case 'lead':
      if (item.doc.status !== 'seeded') return null
      return millis(item.doc.processedAt) || item.createdAt
    case 'feedback':
      if (!item.doc.addressed) return null
      return millis(item.doc.addressedAt) || item.createdAt
    case 'region':
      if (item.doc.status !== 'planned') return null
      return millis(item.doc.resolvedAt) || item.createdAt
    case 'setReport':
    case 'eventReport':
      if (item.doc.status !== 'fixed') return null
      return millis(item.doc.resolvedAt) || item.createdAt
  }
}

function ownerQuery<T>(name: string, field: string, userId: string | null): Query<T> | null {
  if (!userId) return null
  return query(collection(db, name), where(field, '==', userId)) as Query<T>
}

// Bare equality filters only — ordering is client-side so no composite index
// is needed. A serverTimestamp() that hasn't resolved yet reads as 0 and sorts
// first, which is right: it's the thing they just submitted.
export function useMyRequests(userId: string | null): {
  items: RequestItem[]
  loading: boolean
  error: ClassifiedError | null
} {
  const suggestionsRef = useMemo(
    () => ownerQuery<EventSuggestion>('eventSuggestions', 'submittedBy', userId),
    [userId],
  )
  const leadsRef = useMemo(
    () => ownerQuery<ScheduleLead>('scheduleLeads', 'submittedBy', userId),
    [userId],
  )
  const feedbackRef = useMemo(
    () => ownerQuery<Feedback>('feedback', 'userId', userId),
    [userId],
  )
  const regionsRef = useMemo(
    () => ownerQuery<RegionRequest>('regionRequests', 'submittedBy', userId),
    [userId],
  )
  const setReportsRef = useMemo(
    () => ownerQuery<SetReport>('setReports', 'submittedBy', userId),
    [userId],
  )
  const eventReportsRef = useMemo(
    () => ownerQuery<EventReport>('eventReports', 'submittedBy', userId),
    [userId],
  )

  const suggestions = useFirestoreCollection(suggestionsRef)
  const leads = useFirestoreCollection(leadsRef)
  const feedback = useFirestoreCollection(feedbackRef)
  const regions = useFirestoreCollection(regionsRef)
  const setReports = useFirestoreCollection(setReportsRef)
  const eventReports = useFirestoreCollection(eventReportsRef)

  const items = useMemo<RequestItem[]>(() => {
    const all: RequestItem[] = [
      ...suggestions.data.map(doc => ({
        kind: 'suggestion' as const, id: doc.id, createdAt: millis(doc.createdAt), doc,
      })),
      ...leads.data.map(doc => ({
        kind: 'lead' as const, id: doc.id, createdAt: millis(doc.createdAt), doc,
      })),
      ...feedback.data.map(doc => ({
        kind: 'feedback' as const, id: doc.id, createdAt: millis(doc.createdAt), doc,
      })),
      ...regions.data.map(doc => ({
        kind: 'region' as const, id: doc.id, createdAt: millis(doc.createdAt), doc,
      })),
      ...setReports.data.map(doc => ({
        kind: 'setReport' as const, id: doc.id, createdAt: millis(doc.createdAt), doc,
      })),
      ...eventReports.data.map(doc => ({
        kind: 'eventReport' as const, id: doc.id, createdAt: millis(doc.createdAt), doc,
      })),
    ]
    return all.sort((a, b) => {
      if (a.createdAt === 0 || b.createdAt === 0) return a.createdAt - b.createdAt
      return b.createdAt - a.createdAt
    })
  }, [suggestions.data, leads.data, feedback.data, regions.data, setReports.data, eventReports.data])

  const anyLoading =
    suggestions.loading || leads.loading || feedback.loading || regions.loading || setReports.loading ||
    eventReports.loading

  return {
    items,
    loading: anyLoading && items.length === 0,
    error: suggestions.error ?? leads.error ?? feedback.error ?? regions.error ?? setReports.error ??
      eventReports.error,
  }
}
